import { MutableRefObject, ReactEventHandler, useCallback } from "react";
import * as TinyMCETypes from "tinymce";
import { SelectChangeEvent } from "@mui/material";
import {
  clearTemplateInput,
  selectMessageTemplateState,
  setName,
  setShowTemplateDialog,
  setVisibility,
  toggleShowSaveTemplateOptions
} from "../../Slices/MessageDialogTemplateSlice";
import { useAppDispatch, useAppSelector } from "../reduxHooks";
import { useMessageInputState } from "./useMessageInput";

export function useMessageTemplateState() {
  return useAppSelector(selectMessageTemplateState);
}

export function useMessageTemplateHandlers() {
  const dispatch = useAppDispatch();
  const inputState = useMessageInputState();
  const templateState = useMessageTemplateState();

  const handleClearTemplateInputState = () => {
    dispatch(clearTemplateInput());
  };
  const handleValidateTemplateState = useCallback(() => {
    const isTemplateStateValid = Boolean(templateState.name.length && templateState.visibility.length);
    const isInputTextValid = Boolean(inputState.body.length && !inputState.bodyErrorMessage);
    const isEmail = inputState.messageType === "email";
    const isSubjectValid = isEmail
      ? Boolean(inputState.subject?.length && !inputState.subjectErrorMessage)
      : true;
    return isTemplateStateValid && isInputTextValid && isSubjectValid;
  }, [
    inputState.body,
    inputState.bodyErrorMessage,
    inputState.messageType,
    inputState.subject,
    inputState.subjectErrorMessage,
    templateState.name,
    templateState.visibility
  ]);
  const handleToggleTemplateOptions = () => {
    dispatch(toggleShowSaveTemplateOptions());
  };
  const handleShowTemplateDialog = () => {
    dispatch(setShowTemplateDialog(true));
  };
  const handleHideTemplateDialog = () => {
    dispatch(setShowTemplateDialog(false));
  };
  const handleTemplateNameChange = (value: string) => {
    dispatch(setName(value));
  };
  const handleTemplateNameChangeEvent: ReactEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    handleTemplateNameChange(e.currentTarget.value);
  };
  const handleTemplateVisibilityChange = (e: SelectChangeEvent<string>) => {
    dispatch(setVisibility(e.target.value));
  };

  const handleTemplateSelected = <T>(
    template: T,
    updateMessageState: (template: T, editor?: MutableRefObject<TinyMCETypes.Editor | null>) => void,
    editor?: MutableRefObject<TinyMCETypes.Editor | null>
  ) => {
    updateMessageState(template, editor);
    handleHideTemplateDialog();
  };

  return {
    handleClearTemplateInputState,
    handleValidateTemplateState,
    handleToggleTemplateOptions,
    handleShowTemplateDialog,
    handleHideTemplateDialog,
    handleTemplateNameChange,
    handleTemplateNameChangeEvent,
    handleTemplateVisibilityChange,
    handleTemplateSelected
  } as const;
}
